import React, { useEffect, useState, useRef } from "react";
import Loading from "../Modals/Loading";
import scannedItemsService from "../Services/scannedItemsService";
import {
  MdKeyboardDoubleArrowDown,
  MdKeyboardDoubleArrowUp,
} from "react-icons/md";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const StockDetails = () => {
  const [scannedItems, setScannedItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const bottomRef = useRef(null);
  const topRef = useRef(null);

  useEffect(() => {
    const getScannedItems = async () => {
      try {
        setLoading(true);
        const response = await scannedItemsService.fetchAll();
        if (response && response.scanneditems) {
          setScannedItems(response.scanneditems);
        }
      } catch (error) {
        console.error("Error fetching scanned items:", error.message);
      } finally {
        setLoading(false);
      }
    };
    getScannedItems();
  }, []);

  const filteredItems = scannedItems.filter((item) =>
    item.part_number.toLowerCase().startsWith(searchTerm.toLowerCase()),
  );

  const totalQuantity =
    filteredItems.reduce(
      (sum, item) => sum + (Number(item.quantity) || 0),
      0,
    ) || 0;

  const totalPrice =
    filteredItems.reduce(
      (sum, item) => sum + (Number(item.quantity * item.price) || 0),
      0,
    ) || 0;

    const downloadExcel = () => {
  const data = filteredItems.map((item, index) => ({
    "Sr No": item.sr_no || index + 1,
    "Part Number": item.part_number,
    "Name": item.name,
    "Quantity": item.quantity,
    "Maker": item.maker,
    "Condition": item.condition,
    "HS Code": item.hs_code,
    "Price Per Piece": item.price,
    "Total Price": item.quantity * item.price,
  }));

  const worksheet = XLSX.utils.json_to_sheet(data);

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Stock Details");

  const excelBuffer = XLSX.write(workbook, {
    bookType: "xlsx",
    type: "array",
  });

  const file = new Blob([excelBuffer], {
    type: "application/octet-stream",
  });

  saveAs(file, `stock-details-${Date.now()}.xlsx`);
};

  return (
    <main className="mx-10 my-2 p-6 rounded-3xl border border-gray-200 shadow-lg mb-20 bg-white">
      <div ref={topRef} />
      {/* Floating Buttons */}

      <div className="fixed bottom-10 right-10 flex flex-col gap-3 z-50">
        <button
          onClick={() => topRef.current?.scrollIntoView({ behavior: "smooth" })}
          className="w-11 h-11 flex items-center justify-center rounded-full bg-white border border-gray-200 shadow-lg hover:shadow-xl hover:bg-gray-50 hover:-translate-y-1 transition-all duration-300 group"
          title="Scroll to Top"
        >
          <MdKeyboardDoubleArrowUp className="text-xl text-gray-600 group-hover:text-gray-900 transition-colors duration-300" />
        </button>
        <button
          onClick={() =>
            bottomRef.current?.scrollIntoView({ behavior: "smooth" })
          }
          className="w-11 h-11 flex items-center justify-center rounded-full bg-white border border-gray-200 shadow-lg hover:shadow-xl hover:bg-gray-50 hover:translate-y-1 transition-all duration-300 group"
          title="Scroll to Bottom"
        >
          <MdKeyboardDoubleArrowDown className="text-xl text-gray-600 group-hover:text-gray-900 transition-colors duration-300" />
        </button>
      </div>

      {loading ? (
        <Loading />
      ) : (
        <div>
          {/* Top anchor */}

          <div className="flex justify-between items-center mb-6">
            <h1 className="text-3xl font-bold text-secondary">Stock Details</h1>
            <div className="flex flex-row items-center gap-4 w-[40%]">
              <button className="button-primary" onClick={downloadExcel}>
  Download Excel
</button>
              <input
                type="text"
                placeholder="Search by Part Number"
                value={searchTerm}
                onFocus={(e) => e.target.select()}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="primary-input"
              />
            </div>
          </div>

          {filteredItems && filteredItems.length > 0 ? (
            <div className="table-div">
              <table>
                <thead>
                  <tr className="border-y-2 text-left w-full text-black text-[10px]">
                    <th className="text-center py-4 w-[4%] rounded-tl-lg">
                      Sr No.
                    </th>
                    <th className="text-center py-4 w-[2%]">Part Number</th>
                    <th className="text-center py-4 w-[6%]">Name</th>
                    <th className="text-center py-4 w-[6%]">Quantity</th>
                    <th className="text-center py-4 w-[6%]">Maker</th>
                    <th className="text-center py-4 w-[6%]">Condition</th>
                    <th className="text-center py-4 w-[6%]">HS Code</th>
                    <th className="text-center py-4 w-[7%]">Price Per Piece</th>
                    <th className="text-center py-4 w-[6%]">Total Price</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredItems.map((value, index) => (
                    <tr
                      key={index}
                      className="bg-accent border-gray-100 border-b"
                    >
                      <td className="py-4 text-center text-blue-500 font-bold text-[12px] w-[4%]">
                        {value.sr_no}.
                      </td>
                      <td className="py-4 text-center font-semibold text-[12px] w-[2%]">
                        {value.part_number}
                      </td>
                      <td className="py-4 text-center font-semibold text-[12px] w-[6%]">
                        {value.name}
                      </td>
                      <td className="py-4 text-center text-[12px] w-[6%]">
                        {value.quantity}
                      </td>
                      <td className="py-4 text-center text-[12px] w-[6%]">
                        {value.maker}
                      </td>
                      <td className="py-4 text-center text-[12px] w-[6%]">
                        {value.condition}
                      </td>
                      <td className="py-4 text-center text-[12px] w-[6%]">
                        {value.hs_code}
                      </td>
                      <td className="py-4 text-center text-[12px] w-[7%] font-semibold">
                        {Number(value.price).toFixed(2)}
                      </td>
                      <td className="py-4 text-center text-[12px] w-[6%] font-semibold">
                        {(value.quantity * value.price).toFixed(2)}
                      </td>
                    </tr>
                  ))}
                  <tr className="text-[12px] text-lg bg-gray-100 font-semibold text-teal-600 pt-4">
                    <td className="py-3 pl-4 col-span-4 rounded-bl-xl">
                      Total
                    </td>
                    <td />
                    <td />
                    <td className="py-3 text-center">{totalQuantity}</td>
                    <td />
                    <td className="py-3 pl-4" />
                    <td className="py-3 pl-4" />
                    <td className="py-3 pl-4" />
                    <td className="py-3 text-center rounded-br-xl">
                      {totalPrice.toFixed(2)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          ) : (
            <div className="mt-20">
              <h1 className="font-bold text-2xl text-gray-700 text-center">
                No Data Found
              </h1>
            </div>
          )}

          {/* Bottom anchor */}
          <div ref={bottomRef} />
        </div>
      )}
    </main>
  );
};

export default StockDetails;
